<template>
  <Head>
    <title>{{ getValue('meta.title') }}</title>
    <meta
      charset="UTF-8"
    >
    <meta
      http-equiv="Content-Language"
      :content="page.language + '-' + page.country"
    >
    <meta
      http-equiv="Content-Type"
      content="text/html; charset=utf-8"
    >
    <meta
      name="robots"
      content="noindex"
    >
    <meta
      v-if="get('meta')"
      name="description"
      :content="getValue('meta.description')"
    >
    <meta
      v-if="get('meta')"
      name="keywords"
      :content="get('meta.keywords') ? getValue('meta.keywords').join(', ') : ''"
    >
    <meta
      v-if="get('meta')"
      property="og:title"
      :content="getValue('meta.title')"
    >
    <meta
      v-if="get('meta')"
      property="og:description"
      :content="getValue('meta.description')"
    >
    <meta
      property="og:type"
      content="website"
    >
    <meta
      property="og:url"
      :content="pageUrl"
    >
    <meta
      v-if="get('meta')"
      property="og:image"
      :content="getValue('meta.share-image')"
    >
    <meta
      name="format-detection"
      content="telephone=no"
    >
    <meta
      name="viewport"
      content="width=device-width,maximum-scale=1,user-scalable=no"
    >
    <meta
      name="facebook-domain-verification"
      content="7tlnl3bmlwygqq4p4mrqbqg6txt2ws"
    >
    <base :href="baseUrl">
    <link
      rel="shortcut icon"
      :href="getValue('meta.favicon')"
      type="image/x-icon"
    >
  </Head>

  <CustomScript
    v-for="script in scripts['header']"
    :key="script.name"
    :script="script"
  />

  <!-- Google Tag Manager (noscript) -->
  <noscript>
    <iframe
    v-if="gtmEnabled"
    src="https://www.googletagmanager.com/ns.html?id=GTM-NBWK2Z5"
    height="0" width="0"
    style="display:none;visibility:hidden">
    </iframe>
  </noscript>
  <!-- End Google Tag Manager (noscript) -->

  <slot />

  <ZoeOrganismCookieBar
    v-if="showCookieBar && isLanding"
    :style="cookieBarStyle"
    :text="cookiesBar.text"
    :slug="cookiesBar.slug"
    :click-here="cookiesBar.clickHere"
    :button="cookiesBar.button"
  />

  <ZoeOrganismDialog
    v-for="dialog in dialogs"
    :key="dialog.slug"
    :content="dialog"
  />

  <ZoeOrganismDialog
    v-if="phoneNumberVerified"
    key="phone-number-verified"
    :content="{
      slug: 'phone-number-verified',
      title: translations['sms-verification']['success_message_title'],
      content: translations['sms-verification']['success_message_text'],
    }"
  />

  <ZoeOrganismFooter
    v-if="!isThankyou"
    :dialogs="dialogs"
    :footer-text="footerText"
  />

  <CustomScript
    v-for="script in scripts['footer']"
    :key="script.name"
    :script="script"
  />
</template>

<script>
// Libraries
import {Head, usePage} from '@inertiajs/vue3';
import cssHasPseudo from 'css-has-pseudo/browser';
import smoothscroll from 'smoothscroll-polyfill';

// Components
import { addTypography, addButtonStyling, addInputStyling, addContainerStyling } from '@mixins/styling/styling';
import { layerHelper } from '@mixins/utils/data-layer-helper';
import { setupUrlProgressTracking } from '@mixins/utils/url-progress-tracking';
// json5-ignore
import ZoeOrganismCookieBar from '../../../../frontend-templates/resources/components/organisms/cookie-bar/v1.0.0/zoe-organism-cookie-bar.vue';
import ZoeOrganismDialog from '../../../../frontend-templates/resources/components/organisms/dialog/v1.0.0/zoe-oragnism-dialog.vue';
// json5-ignore
import ZoeOrganismFooter from '../../../../frontend-templates/resources/components/organisms/footer/v1.0.0/zoe-organism-footer.vue';
import CustomScript from '../components/custom-script.vue';

// Mixins
import { getParameters } from '../../../../frontend-templates/resources/js/vue-flows/helpers/history/v1/history-1.0.0.js';
import Page from '../mixins/page.vue';
import Validate from '../../../../frontend-templates/resources/js/vue-flows/default-components/sb-validate/v2/Validate.vue';
import Mask from '../../../../frontend-templates/resources/js/vue-flows/default-components/sb-mask/v2/Mask.vue';
import LanguageFile from '../../../../frontend-templates/resources/js/vue-flows/translations/LanguageFile.vue';
import Typography from '../../../../frontend-templates/resources/mixins/typography/typography.vue';
import DefaultVariables from '../components/default-variables.vue';
import UrlVariables from '../mixins/url-variables.vue';
import CroHandler from '../mixins/cro-handler.vue';
import TommiePixel from '../mixins/tommie-pixel.vue';
import DeviceType from '../mixins/device-type.vue';

// Other
import ConditionalTexts from '../../../../frontend-templates/resources/js/flows/plugins/conditional-texts/v1/conditional-texts-1.0.0';
import DesignScripts from '../../../../frontend-templates/resources/js/vue-flows/design-scripts/v1/design-scripts-1.0.0';
import {initializeStoreLocator} from '../mixins/store-locator';

if (!CSS.supports('selector(:has(body))')) {
  cssHasPseudo(document);
}

new DesignScripts();

export default {
  components: {
    Head,
    ZoeOrganismCookieBar,
    ZoeOrganismDialog,
    ZoeOrganismFooter,
    CustomScript,
  },

  mixins: [
    Page,
    Validate,
    Mask,
    LanguageFile,
    Typography,
    DefaultVariables,
    UrlVariables,
    CroHandler,
    TommiePixel,
    DeviceType,
  ],

  props: {
    page: {
      type: Object,
      required: true,
    },

    dialogs: {
      type: Object,
      required: true,
    },

    footerText: {
      type: String,
      required: true,
    },

    samCampaignId: {
      type: String,
      required: true,
    },

    sourceId: {
      type: String,
      required: true,
    },

    scripts: {
      type: Object,
      required: true,
    },

    notificationText: {
      type: String,
      required: true,
    },

    isThankyou: {
      type: Boolean,
      required: true,
    },

    isLanding: {
      type: Boolean,
      required: true,
    },

    leadData: {
      type: Object,
      required: true,
    },

    leadService: {
      type: String,
      required: false,
      default: null,
    },

    recaptcha: {
      type: Object,
      required: false,
      default: null,
    },

    fonts: {
      type: Object,
      required: true,
    },

    cookiesBar: {
      type: Object,
      required: true,
    },

    pageUrl: {
      type: String,
      required: true,
    },

    baseUrl: {
      type: String,
      required: true,
    },

    zipcodeLists: {
      type: Array,
      required: true,
    },

    routes: {
      type: Object,
      required: true,
    },

    translations: {
      type: Object,
      required: true,
    },

    direction: {
      type: String,
      required: true,
    },

    phoneNumberVerified: {
      type: Boolean,
      required: true,
    },

    domain: {
      type: String,
      required: true,
    },

    redirect: {
      type: Object,
      required: false,
      default: null,
    },

    customer: {
      type: Object,
      required: true,
    },

    gtmEnabled: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      questions: [],
    };
  },

  computed: {
    showCookieBar() {
      const cookieBar = this.get('general.cookieBar') || this.get('general.style.cookieBar');
      return this.cookiesBar.show && (cookieBar?.show?.value || false);
    },

    cookieBarStyle() {
      const cookieBarStyle = this.get('general.cookieBar.style') || this.get('general.style.cookieBar');
      let style;

      if (cookieBarStyle) style = cookieBarStyle;

      if (style.button) {
        Object.entries(style.button).forEach(([key, value]) => {
          style[`button-${key}`] = value;
        });
      }

      return style;
    },
  },

  created() {
    const pageData = this.transformPageData(this.page.data);

    this.setPageData(pageData);
    this.setQuizData(pageData);
    this.setFieldData();
    this.setGeneralStyling(pageData.style);
    this.addStyling();
    this.setLanguageFile();
    this.removeBEStyling();
    this.setCountryMask();
    setupUrlProgressTracking();

    smoothscroll.polyfill();
  },

  mounted() {
    this.$store.commit('page/setValue', { key: 'browserBackButton', value: false });
    this.$store.commit('quiz/setQuiz', { key: 'browserBackButton', value: false });
    this.$store.commit('page/setValue', {key: 'wrapper', value: document.getElementById('wrapper')});

    if (this.recaptcha && this.recaptcha.siteKey) {
      this.$store.commit('page/setValue', { key: 'recaptchaKey', value: this.recaptcha.siteKey });
    }

    if (Object.keys(this.fonts['google']).length > 0) {
      for (const font of Object.keys(this.fonts['google'])) {
        const formattedFont = 'google---' + font.split(':')[0];
        window.$loadFont(formattedFont, 'google');
      }
    }

    this.$nextTick(() => {
      new ConditionalTexts();
      layerHelper();
      initializeStoreLocator();
    });

    document.body.setAttribute('dir', this.direction);

    for (const fontItem of Object.values(this.fonts['zoe'])) {
      for (const fontFace of fontItem) {
        window.$loadFont(fontFace, 'zoe', font => {
          const style = document.createElement('style');
          style.innerHTML = `
              @font-face {
                  font-family: '${font.identifier}';
                  font-style: ${font.style};
                  font-weight: ${font.weight};
                  font-display: swap;
                  src: url("${font.woff2}") format("woff2"), url("${font.woff}") format("woff");
              }`;
          document.head.appendChild(style);
        });
      }
    }

    if (this.gtmEnabled) {
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','GTM-NBWK2Z5');
    } else {
      const firstScript = document.getElementsByTagName('script')[0];
      const scriptParentNode = firstScript.parentNode;
      const fallbackGtmScript = document.createElement('script');
      fallbackGtmScript.innerHTML = 'window.dataLayer = window.dataLayer || [];';
      scriptParentNode.insertBefore(fallbackGtmScript, firstScript);
    }

    this.setCountryValidator();
    this.fillUrlVariables();

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timeZone) {
        this.$store.commit('form/addField', {
            name: 'timezone',
            data: {
                name: 'timezone',
                value: timeZone,
            },
        });
    }

    if (this.phoneNumberVerified) {
      this.$store.commit('dialog/show', 'phone-number-verified');
    }

    if (this.redirect.url && this.leadData?.eventId) {
        setTimeout(() => {
            let url = `${this.redirect.url}/?qcat=${this.leadData.eventId}`;
            const version = this.leadData.enquiry_dashboard_redirect_version || this.redirect.version;
            if (version) {
                url += `&v=${version}`;
            }
            window.location.href = url;
        }, 3000);
    }

    this.updateMobileBaseSize();
  },

  methods: {
    setInnerHTML(elm, html) {
      elm.innerHTML = html;

      Array.from(elm.querySelectorAll('script')).forEach( oldScriptEl => {
        const newScriptEl = document.createElement('script');

        Array.from(oldScriptEl.attributes).forEach( attr => {
          newScriptEl.setAttribute(attr.name, attr.value);
        });

        const scriptText = document.createTextNode(oldScriptEl.innerHTML);
        newScriptEl.appendChild(scriptText);

        oldScriptEl.parentNode.replaceChild(newScriptEl, oldScriptEl);
      });
    },

    addHiddenFormFields() {
      this.$store.commit('form/addField', {
        name: 'sam_campaign_id',
        data: { name: 'sam_campaign_id' },
      });
      this.$store.commit('form/setFieldData', { name: 'sam_campaign_id', key: 'value', value: this.samCampaignId.toString() });

      if (this.sourceId) {
          this.$store.commit('form/addField', {
              name: 'source_id',
              data: {
                  name: 'source_id',
              },
          });

          this.$store.commit('form/setFieldData', {
              name: 'source_id',
              key: 'value',
              value: (this.sourceId ?? '').toString(),
          });
      }

      const parameters = getParameters();
      const options = {
        appid: 'ap_program_id',
        jid: 'jid',
        traffic_source_id: 'traffic_source_id',
        aacri: 'aacri',
        tscid: 'tscid',
        aaci: 'aaci',
        worktype_id: 'worktype_id',
      };

      for (const [key, value] of Object.entries(options)) {
        if (parameters[key] !== undefined) {
          this.$store.commit('form/addField', {
            name: value,
            data: {name: value},
          });
          this.$store.commit('form/setFieldData', {name: value, key: 'value', value: parameters[key]});
        }
      }
    },

    transformPageData(object, parentKey = '') {
      const newObject = Array.isArray(object) ? [] : {};

      for (const key in object) {
        const value = object[key];

        if (value && value['editor-type'] === 'QuizSection') {
          const quizSection = this.transformPageData(value.items.questions.items, 'questions');
          quizSection.forEach(question => {
            this.questions.push({
              ...question,
              step: parentKey,
            });
          });
        }

        if (typeof value === 'object' && value !== null) {
          if (value['items']) {
            if (Array.isArray(value.items)) {
              newObject[key] = this.transformPageData(value.items, key);
            } else {
              newObject[key] = {
                _info:
                  Object.keys(value).filter(key => !key.includes('items'))
                    .reduce((obj, key) => {
                      obj[key] = value[key];
                      return obj;
                    }, {}),

                ...this.transformPageData(value.items, key),
              };
            }
          } else {
            newObject[key] = this.transformPageData(value);
          }
        } else {
          if (value === 'true' || value === 'false') {
            newObject[key] = value === 'true';
            continue;
          }

          newObject[key] = value;
        }
      }

      return newObject;
    },

    setPageData(data) {
      this.$store.commit('page/setValue', {key: 'notificationText', value: this.notificationText});
      this.$store.commit('page/setValue', {key: 'productType', value: this.page.productType});
      this.$store.commit('page/setValue', {key: 'leadService', value: this.leadService});
      this.$store.commit('page/setValue', {key: 'leadData', value: this.leadData});
      this.$store.commit('page/setData', data);
      this.$store.commit('page/setCountryCode', this.page.countryCode);
      this.$store.commit('page/setLanguage', this.page.language);
      this.$store.commit('page/setCustomer', this.page.customer);
      this.$store.commit('page/setZipcodeLists', this.zipcodeLists);
      this.$store.commit('page/setPreview', data.tag);
      this.$store.commit('page/setRoutes', this.routes);
      this.$store.commit('page/setTranslations', this.translations);
      this.$store.commit('page/setPageGroupType', this.page.pageGroupType);
      this.$store.commit('page/setValue', { key: 'isLanding', value: this.isLanding });
      this.$store.commit('page/setValue', { key: 'domain', value: this.domain });
        this.$store.commit('page/setValue', { key: 'needsTrustedForm', value: this.customer.needsTrustedForm});
        this.$store.commit('page/setValue', { key: 'isFccSubject', value: this.customer.isFccSubject});
    },

    setQuizData() {
      if (this.questions.length === 0) return;
      const quizObject = this.get('quiz.quiz') || this.get('start.quiz') || this.get('step.quiz');
      if (!quizObject) return;

      this.$store.commit('quiz/setQuiz', {key: 'questionsData', value: this.questions });
      this.$store.commit('quiz/setQuiz', { key: 'advancedWorktypeSettings', value: quizObject['worktype-override'] || [] });
      this.$store.commit('quiz/setQuiz', { key: 'advancedFlow', value: quizObject.advanced });

      if (quizObject.continuous) {
        this.$store.commit('quiz/setQuiz', { key: 'continuousFlow', value: quizObject.continuous });
      }

    },

    setFieldData() {
      this.$store.commit('form/addField', {
        name: 'sam_campaign_id',
        data: { name: 'sam_campaign_id' },
      });
      this.$store.commit('form/setFieldData', { name: 'sam_campaign_id', key: 'value', value: this.samCampaignId.toString() });

      if (this.sourceId) {
          this.$store.commit('form/addField', {
              name: 'source_id',
              data: {
                  name: 'source_id',
              },
          });
          this.$store.commit('form/setFieldData', {
              name: 'source_id',
              key: 'value',
              value: (this.sourceId ?? '').toString(),
          });
      }

      const page = usePage();

      if (page.props.sms_verification) {
        this.$store.commit('form/addField', {
          name: 'phone_number_verified',
          data: {
            name: 'phone_number_verified',
          },
        });

        this.$store.commit('form/setFieldData', {
          name: 'phone_number_verified',
          key: 'value',
          value: 0,
        });
      }

      const parameters = getParameters();
      const options = {
        appid: 'ap_program_id',
        jid: 'jid',
        traffic_source_id: 'traffic_source_id',
        aacri: 'aacri',
        tscid: 'tscid',
        aaci: 'aaci',
        worktype_id: 'worktype_id',
        origin: 'origin',
        gclid: 'gclid',
      };

      for (const [key, value] of Object.entries(options)) {
        if (parameters[key] !== undefined) {
          this.$store.commit('form/addField', {
            name: value,
            data: {name: value},
          });
          this.$store.commit('form/setFieldData', {name: value, key: 'value', value: decodeURIComponent(parameters[key])});
        }
      }
    },

    setGeneralStyling(data) {
      if (data && data.length !== 0) {
        addTypography(data.style.typography);
        addButtonStyling(data.style.buttons);
        addInputStyling(data.style.form_fields);
        addContainerStyling(data.style.containers);
      }
    },

    addStyling() {
      const element = document.createElement('style');
      let styling = this.page.style;

      styling = styling.split(';');

      let styleObject = {};

      // filter out all keys not ending with '-key'
      styling.forEach(item => {
        let key = item.split(':')[0];
        const value = item.split(':')[1];

        if (!key.endsWith('-value')) return;

        key = key.replaceAll('-items', '');
        key = key.replaceAll(/-value$/gi, '');

        styleObject[key] = value;
      });

      styling = Object.entries(styleObject).map(([key, value]) => {
        return `${key}: ${value};`;
      }).join('');

      element.innerText = ':root {' + styling + '}';

      document.head.appendChild(element);
    },

    removeBEStyling () {
      if (document.querySelector('.be-styling')) {
        document.querySelector('.be-styling').remove();
      }
    },

    updateMobileBaseSize() {
      document.documentElement.style.setProperty('--mobile-base-size', (window.innerWidth / 640).toString());
      window.addEventListener('resize', () => {
        document.documentElement.style.setProperty('--mobile-base-size', (window.innerWidth / 640).toString());
      });
    },
  },
};
</script>

<style lang="scss">
@import '../../../../frontend-templates/resources/scss/shared';
</style>
